/* eslint-disable no-restricted-syntax */
/* eslint-disable consistent-return */
import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { IconCheveronLeft, IconCheveronRight } from '../Icons';
import './ScrollableTabs.css';

const ScrollableTabs = ({ onClick, active, tabs, scrollable = false }) => {
  const tabsRef = useRef([]);
  const containerRef = useRef();
  const [hasLeftTabs, setHasLeftTabs] = useState(false);
  const [hasRightTabs, setHasRightTabs] = useState(false);
  const [maxWidth, setMaxWidth] = useState(0);

  useEffect(() => {
    setMaxWidth(Math.max(...tabsRef.current.map(el => el.offsetWidth)));
  }, []);

  const handleUpdate = () => {
    const { right: containerRight, left: containerLeft } =
      containerRef.current.getBoundingClientRect();
    let hasLeft = false;
    let hasRight = false;
    for (const tab of tabsRef.current) {
      const { right, left } = tab.getBoundingClientRect();
      // -1/1 because in most leftist/rightist position,
      // the two values have some minimal variation
      if (left - containerLeft < -1) {
        hasLeft = true;
      }
      if (right - containerRight > 1) {
        hasRight = true;
      }
      if (hasLeft && hasRight) break;
    }
    setHasLeftTabs(hasLeft);
    setHasRightTabs(hasRight);
  };

  let timeout;
  const triggerUpdate = () => {
    clearTimeout(timeout);
    timeout = setTimeout(handleUpdate, 200);
  };

  useEffect(() => {
    triggerUpdate();
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const scroll = index => {
    const current = tabsRef.current[index];
    const offset = current.offsetLeft - 30 - containerRef.current.offsetLeft;

    if (containerRef.current.scrollTo) {
      containerRef.current.scrollTo({ left: offset, top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (active && tabs.length) {
      const index = tabs.map(({ key }) => key).indexOf(active);
      if (index > -1) scroll(tabs.map(({ key }) => key).indexOf(active));
    }
  }, [active, tabs]);

  useEffect(() => {
    containerRef.current.addEventListener('scroll', triggerUpdate);
    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('scroll', triggerUpdate);
      }
    };
  }, []);

  useEffect(() => {
    window.addEventListener('resize', triggerUpdate);
    return () => {
      window.removeEventListener('resize', triggerUpdate);
    };
  }, []);

  const handleClick = isRightClick => () => {
    containerRef.current.scrollBy({
      left: isRightClick ? maxWidth : -maxWidth,
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <div className="flex flex-1 overflow-hidden">
      {scrollable && (
        <button
          type="button"
          onClick={handleClick()}
          className={`flex flex-col items-center justify-center text-slate-600 px-1 focus:outline-none ${
            hasLeftTabs ? '' : 'opacity-0 pointer-events-none'
          }`}
        >
          <IconCheveronLeft />
        </button>
      )}

      <div className="flex overflow-x-auto no-scrollbar" ref={containerRef}>
        {tabs.map(({ key, label, additionalChild, disabled }, i) => (
          <button
            id={key}
            type="button"
            key={key}
            ref={el => {
              tabsRef.current[i] = el;
            }}
            onClick={() => onClick(key)}
            disabled={disabled}
            className={`px-3 flex flex-shrink-0 whitespace-nowrap focus:outline-none border-b-4 items-center disabled:cursor-not-allowed ${
              active === key
                ? 'text-slate font-semibold border-blue border-blue-600'
                : 'text-slate-700 border-slate-50'
            }`}
          >
            {label}
            {additionalChild}
          </button>
        ))}
      </div>
      {scrollable && (
        <button
          type="button"
          onClick={handleClick(true)}
          className={`flex flex-col items-center justify-center text-slate-600 px-1 focus:outline-none ${
            hasRightTabs ? '' : 'opacity-0 pointer-events-none'
          }`}
        >
          <IconCheveronRight />
        </button>
      )}
    </div>
  );
};

ScrollableTabs.propTypes = {
  onClick: PropTypes.func,
  active: PropTypes.string,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      additionalChild: PropTypes.node,
      disabled: PropTypes.bool,
    }),
  ),
  scrollable: PropTypes.bool,
};

ScrollableTabs.defaultProps = {
  tabs: [],
  onClick: () => () => {},
};

export default ScrollableTabs;
